import React, { FC, useMemo, useState } from "react";

import { VnBox } from "@/components/VnBox";
import { VnButton } from "@/components/VnButton/VnButton";
import { VnText } from "@/components/VnText/VnText";
import { BaseTextInput } from "@/components/VnTextInput/BaseTextInput";
import { IncomePeriodType, IncomeRangeType } from "@/domain/eligibility";
import { useTranslate } from "@/hooks/useTranslate";
import { formatCurrency } from "@/utils/currency";

type HouseholdIncomeCalculatorPropsType = {
  householdMemberNames: string[];
  incomePeriod: IncomePeriodType;
  incomeRanges: IncomeRangeType[];
  onUseRange?: (rangeValue: IncomeRangeType) => void;
};

export const HouseholdIncomeCalculator: FC<
  HouseholdIncomeCalculatorPropsType
> = ({ householdMemberNames, incomePeriod, incomeRanges, onUseRange }) => {
  const [memberIncomes, setMemberIncomes] = useState<Record<string, number>>(
    {},
  );

  const periodLabelI18nKey =
    incomePeriod === "ANNUAL"
      ? "native:eligibilityHouseholdIncomeScreen.householdIncomeCalculator.period.annual"
      : incomePeriod === "BI_WEEKLY"
        ? "native:eligibilityHouseholdIncomeScreen.householdIncomeCalculator.period.biWeekly"
        : "native:eligibilityHouseholdIncomeScreen.householdIncomeCalculator.period.monthly";
  const periodLabel = useTranslate(periodLabelI18nKey) ?? "";

  const totalIncome = useMemo(() => {
    return householdMemberNames.reduce((sum, name) => {
      return sum + (memberIncomes[name] ?? 0);
    }, 0);
  }, [memberIncomes, householdMemberNames]);

  const matchingRange = useMemo(() => {
    return incomeRanges.find(range => {
      if (range.max === null) {
        return totalIncome >= range.min;
      }
      return totalIncome >= range.min && totalIncome <= range.max;
    });
  }, [totalIncome, incomeRanges]);

  const matchingRangeLabel = useMemo(() => {
    if (!matchingRange) return "";
    return matchingRange.value;
  }, [matchingRange]);

  const handleIncomeChange = (
    memberName: string,
    value: string | number | undefined,
  ) => {
    let numValue = 0;

    if (typeof value === "number") {
      numValue = isNaN(value) ? 0 : value;
    } else if (typeof value === "string") {
      const trimmed = value.trim();
      numValue = trimmed === "" ? 0 : parseFloat(trimmed) || 0;
    }

    setMemberIncomes(prev => ({
      ...prev,
      [memberName]: numValue,
    }));
  };

  const handleUseRange = () => {
    if (matchingRange) {
      onUseRange?.(matchingRange);
    }
  };

  return (
    <VnBox className="border-border bg-secondary gap-4 rounded-md border p-5 shadow-sm shadow-black/5">
      <VnText
        variant="lgMedium"
        i18nKey="native:eligibilityHouseholdIncomeScreen.householdIncomeCalculator.title"
      />
      <VnText
        variant="lg"
        className="text-muted-foreground"
        i18nKey={
          incomePeriod === "ANNUAL"
            ? "native:eligibilityHouseholdIncomeScreen.householdIncomeCalculator.annualDescription"
            : incomePeriod === "BI_WEEKLY"
              ? "native:eligibilityHouseholdIncomeScreen.householdIncomeCalculator.biWeeklyDescription"
              : "native:eligibilityHouseholdIncomeScreen.householdIncomeCalculator.monthlyDescription"
        }
      />

      <VnBox className="gap-3">
        {householdMemberNames.map(memberName => {
          return (
            <VnBox key={memberName} className="gap-2">
              <VnText variant="lg">{memberName}</VnText>
              <VnBox className="w-full flex-row items-center gap-3">
                <VnText variant="lg" className="text-muted-foreground shrink-0">
                  $
                </VnText>
                <VnBox className="flex-1">
                  <BaseTextInput
                    isNumber
                    value={memberIncomes[memberName]?.toString() ?? ""}
                    onChangeText={(text: string) => {
                      handleIncomeChange(memberName, text);
                    }}
                    placeholder="0"
                    keyboardType="numeric"
                  />
                </VnBox>
                <VnText variant="lg" className="text-muted-foreground shrink-0">
                  /{periodLabel}
                </VnText>
              </VnBox>
            </VnBox>
          );
        })}
      </VnBox>

      <VnBox className="border-border border-t pt-4">
        <VnBox className="flex-row items-center justify-between">
          <VnText
            variant="lgMedium"
            i18nKey="native:eligibilityHouseholdIncomeScreen.householdIncomeCalculator.totalIncomeTitle"
          />
          <VnText
            variant="lgMedium"
            i18nKey="native:eligibilityHouseholdIncomeScreen.householdIncomeCalculator.totalIncomeValue"
            i18nOptions={{
              total: formatCurrency(totalIncome),
              period: periodLabel,
            }}
          />
        </VnBox>
      </VnBox>

      {matchingRange && (
        <VnButton
          textVariant="lgMedium"
          onPress={handleUseRange}
          i18nKey="native:eligibilityHouseholdIncomeScreen.householdIncomeCalculator.useRange"
          i18nOptions={{ label: matchingRangeLabel }}
        />
      )}
    </VnBox>
  );
};
